import { AttractionTypesList } from '@/components/attraction-types';
import {
  attractionCategoryCrudConfig,
  type AttractionCategory,
} from '@/config/objects/attraction-categories';
import { attractionTypeCrudConfig, type AttractionType } from '@/config/objects/attraction-types';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
  type CrudConfig,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Attraction Category detail page
 */
export function AttractionCategoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [attractionCategory, setAttractionCategory] = React.useState<AttractionCategory | null>(
    null,
  );

  /**
   * Delete attraction category mutation
   */
  const deleteMutation = useMutation({
    mutationFn: async (
      attractionCategoryId: string,
    ): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attraction-categories/${attractionCategoryId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        // Invalidate attraction categories list to refresh data
        queryClient.invalidateQueries({ queryKey: ['AttractionCategory'] });
        navigate('/attraction-categories');
      } else {
        throw new Error(response.error || 'Failed to delete attraction category');
      }
    },
  });

  // Create custom config for attraction category-specific attraction types
  const attractionCategoryAttractionTypesConfig: CrudConfig<AttractionType> = useMemo(
    () => ({
      ...attractionTypeCrudConfig,
      entityName: 'Attraction Type',
      entityNamePlural: 'Attraction Types',
      endpoints: {
        ...attractionTypeCrudConfig.endpoints,
        list: `/attraction-categories/${id}/attraction-types`,
        delete: (attractionTypeId: string) =>
          `/attraction-categories/${id}/attraction-types/${attractionTypeId}`,
      },
      listView: {
        ...attractionTypeCrudConfig.listView,
        actions: {
          add: {
            enabled: true,
            endpoint: '/attraction-types',
            // Filter to exclude attraction types already in this category
            where: attractionCategory
              ? {
                  categories: {
                    none: {
                      attractionCategoryId: attractionCategory.id,
                    },
                  },
                }
              : undefined,
            onAdd: async (attractionType: AttractionType) => {
              await apiClient.post(`/attraction-categories/${id}/attraction-types`, {
                attractionTypeId: attractionType.id,
              });
              toast.success('Attraction type added to attraction category');
            },
          },
          view: {
            enabled: true,
          },
          remove: {
            enabled: true,
            onRemove: async (attractionType: AttractionType) => {
              await apiClient.delete(
                `/attraction-categories/${id}/attraction-types/${attractionType.id}`,
              );
              toast.success('Attraction type removed from attraction category');
            },
          },
        },
      },
    }),
    [attractionCategory, id, apiClient],
  );

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Attraction Category Not Found</h2>
          <p className="text-muted-foreground">
            The requested attraction category could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  /**
   * Navigate to edit form for this attraction category
   */
  const handleEdit = (attractionCategory: AttractionCategory) => {
    navigate(`/attraction-categories/${attractionCategory.id}/edit`);
  };

  /**
   * Handle attraction category deletion
   */
  const handleDelete = async (attractionCategory: AttractionCategory) => {
    return toast.promise(deleteMutation.mutateAsync(attractionCategory.id), {
      loading: 'Deleting attraction category...',
      success: 'Attraction category deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete attraction category',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attraction Categories', href: '/attraction-categories' },
    { label: attractionCategory?.name || 'Attraction Category', current: true },
  ];

  return (
    <PageContainer>
      <div className="space-y-6">
        <CrudDetailView
          config={attractionCategoryCrudConfig}
          itemId={id}
          breadcrumbs={breadcrumbs}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onData={(attractionCategoryData: AttractionCategory) =>
            setAttractionCategory(attractionCategoryData)
          }
        />

        {/* Attraction Category Attraction Types Section */}
        <AttractionTypesList
          config={attractionCategoryAttractionTypesConfig}
          className="p-0"
          baseUrl="/attraction-types"
        />
      </div>
    </PageContainer>
  );
}
